import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import 'rxjs/add/operator/do';
import { Router } from '@angular/router';

import { JwtService, UserService, ModalService } from '../services';
import { TranslateService } from '@ngx-translate/core';
import { requestsTimeFormatV2, timeFormatMismatch } from './time-format-v2-routes';

@Injectable()
export class HttpTokenInterceptor implements HttpInterceptor {
  constructor(
    private jwtService: JwtService,
    private userService: UserService,
    private modalService: ModalService,
    private translateService: TranslateService,
    public router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const headersConfig: any = {};

    const token = this.jwtService.getToken();

    if (token) {
      headersConfig['X-Access-Token'] = token;
    }

    // Dla FormData (multipart — obecnie jedyny taki przypadek to import pacjentów z Excela)
    // NIE ustawiamy Content-Type: przeglądarka sama doda 'multipart/form-data' z właściwym
    // boundary. Wcześniej wymuszany 'application/json' nadpisywał ten nagłówek, więc backend
    // nie mógł sparsować body i zwracał 400. Zwykłe żądania JSON dostają Content-Type jak dotąd.
    if (!(req.body instanceof FormData)) {
      headersConfig['Content-Type'] = 'application/json';
    }

    const timeFormatV2 = requestsTimeFormatV2(req.method, req.url);

    if (timeFormatV2) {
      headersConfig['X-Time-Format'] = '2';
    }

    const request = req.clone({ setHeaders: headersConfig });

    return next.handle(request).do((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse && event.body !== null && event.body !== '') {
        const served = event.headers.get('X-Time-Format');
        const mismatch = timeFormatMismatch(timeFormatV2, served);
        if (mismatch === 'not-migrated') {
          console.error(`X-Time-Format: requested 2, served ${served}: ${req.method} ${req.url}`);
        } else if (mismatch === 'not-opted-in') {
          console.warn(`X-Time-Format: served 1, not requested: ${req.method} ${req.url}`);
        }
      }
    }, (err: any) => {
      if (err instanceof HttpErrorResponse) {

        // Martwa sesja to na tym backendzie WYŁĄCZNIE: HTTP 400 + errors === 'Token is expired'.
        // Cały firewall ^/api obsługuje jeden onAuthenticationFailure, więc kształt jest ten sam
        // na każdym endpoincie (ten sam komunikat leci też, gdy brakuje nagłówka X-Access-Token).
        //
        // HTTP 403 znaczy DOKŁADNIE ODWROTNIE: sesja żyje, brakuje uprawnień do tego zasobu —
        // takie błędy oddajemy wołającemu. Wcześniejszy warunek `err.error.status === 403`
        // czytał pole `status` z CIAŁA odpowiedzi, więc odmowa dostępu do jednego zasobu kasowała
        // całą sesję: pacjent dostawał 403 z /chat/* i wylatywał z aplikacji przy każdym wejściu.
        //
        // Ciało bywa HTML-em (403 z access_control) albo tablicą, stąd sprawdzenie typu —
        // odczyt `.status` wprost na `null` wywracał się tu wyjątkiem.
        const body = err.error && typeof err.error === 'object' ? err.error : {};

        // Docelowym sygnałem jest `error_code` — backend dokłada je obok istniejących pól
        // (HTTP nadal 400, `status` i `errors` bez zmian). Dopasowanie po treści zostaje jako
        // zapas, dopóki nowy kontrakt nie jest wdrożony na obu środowiskach; można je usunąć,
        // gdy dev i prod będą zgodne.
        const sessionInvalid = err.status === 400 &&
          (body.error_code === 'SESSION_INVALID' || body.errors === 'Token is expired');

        if (sessionInvalid) {
          this.userService.purgeAuth();

          if (!err.url.includes('/user/me')) {
            this.router.navigateByUrl('/auth/login');
          }
        } else if (err.status === 500) {
          this.modalService.open(this.translateService.instant('BACKEND.SOMETHING-WRONG'), null, true, 'EVENTS.SHARED.INTERCEPTORS.HTTP.TOKEN.INTERCEPTOR.POPUP.BACKEND-SOMETHING-WRONG');
        }

      }
    });

  }
}
